<template>
    <div class="image-watermark-tool">
        <h3>图片添加水印（可自由拖动位置）</h3>

        <div class="upload-area">
            <input type="file" accept="image/*" @change="handleImageUpload" />
            <p>支持 JPG、PNG、WebP 等格式</p>
        </div>

        <div v-if="imageUrl" class="controls">
            <div class="form-group">
                <label>水印文字：</label>
                <textarea v-model="watermarkText" placeholder="请输入水印文字，支持多行" rows="3"></textarea>
            </div>

            <div class="form-row">
                <div class="form-group">
                    <label>字体大小：</label>
                    <input type="number" v-model.number="fontSize" min="12" max="100" /> px
                </div>

                <div class="form-group">
                    <label>透明度：</label>
                    <input type="range" v-model.number="opacity" min="0.1" max="1" step="0.1" />
                    <span>{{ opacity }}</span>
                </div>
            </div>

            <div class="form-row">
                <div class="form-group">
                    <label>颜色：</label>
                    <input type="color" v-model="color" />
                </div>
            </div>

            <p class="tip">💡 提示：点击并拖动预览图中的水印文字，可自由调整位置</p>
        </div>

        <div v-if="imageUrl" class="preview-container">
            <h4>预览（拖动水印调整位置）：</h4>
            <div class="preview-wrapper" ref="previewWrapper">
                <img :src="imageUrl" alt="原图" class="preview-image" @load="onImageLoad" />
                <div
                        class="draggable-watermark"
                        :style="watermarkStyle"
                        @mousedown="startDrag"
                        @touchstart="startDragTouch"
                        @touchmove="onDragTouch"
                        @touchend="endDrag"
                >
                    {{ watermarkText }}
                </div>
            </div>
        </div>

        <button v-if="previewUrl" @click="downloadImage" class="download-btn">
            ⬇ 下载带水印的图片
        </button>

        <!-- 内容说明区 -->
        <div class="tool-content">
            <h2>🔖 图片添加水印工具</h2>
            <p>为图片添加文字水印，保护版权、标识来源。支持自由拖动水印位置、调整字体大小、颜色、透明度，支持多行文字。</p>

            <h3>✨ 功能特点</h3>
            <ul>
                <li><strong>自由定位</strong>：鼠标/触摸拖动，任意位置放置水印</li>
                <li><strong>样式丰富</strong>：可调字体大小、颜色、透明度</li>
                <li><strong>多行支持</strong>：输入多行文字，自动换行显示</li>
                <li><strong>实时预览</strong>：调整参数即时看到效果</li>
            </ul>

            <h3>📝 使用教程</h3>
            <ol>
                <li>上传需要加水印的图片</li>
                <li>在输入框输入水印文字（支持多行）</li>
                <li>调整字体大小、颜色、透明度</li>
                <li>拖动水印到合适位置</li>
                <li>点击「下载图片」保存</li>
            </ol>

            <h3>💡 水印建议</h3>
            <p><strong>版权声明</strong>：© 2024 你的名字 | www.example.com<br>
            <strong>社交媒体</strong>：@你的账号 | 品牌名称<br>
            <strong>透明度建议</strong>：0.3-0.5 既可见又不遮挡主体</p>

            <h3>🔒 隐私说明</h3>
            <p>使用 Canvas API 在浏览器本地合成，图片不会上传到服务器。</p>
        </div>
    </div>
</template>

<script>
    import seoMixin from '../js/seo-mixin';

export default {
        name: 'ImageWatermark',
        mixins: [seoMixin],
        data() {
            return {
                seoTitle: '图片添加水印工具 - FreeToolBox',
                seoDescription: '免费在线图片水印工具，支持文字水印和图片水印。可自由拖动水印位置，调整字体大小、颜色、透明度。浏览器本地处理，保护隐私。',
                seoKeywords: '图片水印,添加水印,水印工具,版权保护,在线水印',
                imageUrl: '',
                previewUrl: '',
                watermarkText: '© 我的版权',
                fontSize: 24,
                color: '#ffffff',
                opacity: 0.7,
                watermarkPosition: { x: 50, y: 50 },
                isDragging: false,
                startPos: { x: 0, y: 0 },
                imageRect: { width: 0, height: 0, left: 0, top: 0 },
                originalImageSize: { width: 0, height: 0 },
                scale: 1,
            };
        },
        computed: {
            watermarkStyle() {
                return {
                    fontSize: this.fontSize + 'px',
                    color: this.color,
                    opacity: this.opacity,
                    fontWeight: 'bold',
                    textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                    left: this.watermarkPosition.x + '%',
                    top: this.watermarkPosition.y + '%',
                    transform: 'translate(-50%, -50%)',
                };
            },
        },
        methods: {
            handleImageUpload(e) {
                const file = e.target.files[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = (loadEvent) => {
                    this.imageUrl = loadEvent.target.result;
                };
                reader.readAsDataURL(file);
            },

            onImageLoad(e) {
                const img = e.target;
                //const wrapper = this.$refs.previewWrapper;

                // 保存原始图片尺寸
                this.originalImageSize = {
                    width: img.naturalWidth,
                    height: img.naturalHeight,
                };

                // 获取显示尺寸
                this.imageRect = {
                    width: img.offsetWidth,
                    height: img.offsetHeight,
                    left: img.offsetLeft,
                    top: img.offsetTop,
                };

                // 计算缩放比例
                this.scale = img.offsetWidth / img.naturalWidth;

                // 水印初始位置设为正中间（百分比）
                this.watermarkPosition = {
                    x: 50,
                    y: 50,
                };

                this.updatePreview();
            },

            startDrag(e) {
                e.preventDefault();
                e.stopPropagation();
                this.isDragging = true;
                this.startPos = {
                    x: e.clientX,
                    y: e.clientY,
                };

                document.addEventListener('mousemove', this.onDrag);
                document.addEventListener('mouseup', this.endDrag);
            },

            onDrag(e) {
                if (!this.isDragging) return;

                const wrapper = this.$refs.previewWrapper;
                const wrapperRect = wrapper.getBoundingClientRect();

                // 计算鼠标在wrapper中的相对位置（百分比）
                const x = ((e.clientX - wrapperRect.left) / wrapperRect.width) * 100;
                const y = ((e.clientY - wrapperRect.top) / wrapperRect.height) * 100;

                // 限制范围 5%-95%
                this.watermarkPosition.x = Math.max(5, Math.min(95, x));
                this.watermarkPosition.y = Math.max(5, Math.min(95, y));

                // 更新预览
                this.updatePreview();
            },

            endDrag() {
                this.isDragging = false;
                document.removeEventListener('mousemove', this.onDrag);
                document.removeEventListener('mouseup', this.endDrag);
            },

            // 移动端触摸支持
            startDragTouch(e) {
                e.preventDefault();
                e.stopPropagation();
                this.isDragging = true;
                const touch = e.touches[0];
                this.startPos = {
                    x: touch.clientX,
                    y: touch.clientY,
                };

                document.addEventListener('touchmove', this.onDragTouch, { passive: false });
                document.addEventListener('touchend', this.endDragTouch);
            },

            onDragTouch(e) {
                if (!this.isDragging) return;
                e.preventDefault();

                const wrapper = this.$refs.previewWrapper;
                const wrapperRect = wrapper.getBoundingClientRect();
                const touch = e.touches[0];

                const x = ((touch.clientX - wrapperRect.left) / wrapperRect.width) * 100;
                const y = ((touch.clientY - wrapperRect.top) / wrapperRect.height) * 100;

                this.watermarkPosition.x = Math.max(5, Math.min(95, x));
                this.watermarkPosition.y = Math.max(5, Math.min(95, y));

                this.updatePreview();
            },

            endDragTouch() {
                this.isDragging = false;
                document.removeEventListener('touchmove', this.onDragTouch);
                document.removeEventListener('touchend', this.endDragTouch);
            },

            updatePreview() {
                if (!this.imageUrl) return;

                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');

                    canvas.width = img.width;
                    canvas.height = img.height;

                    ctx.drawImage(img, 0, 0);

                    if (!this.watermarkText.trim()) {
                        this.previewUrl = canvas.toDataURL('image/png');
                        return;
                    }

                    // 计算水印在原图上的位置（根据百分比）
                    const x = (this.watermarkPosition.x / 100) * img.width;
                    const y = (this.watermarkPosition.y / 100) * img.height;

                    ctx.font = `bold ${this.fontSize * 2}px Arial, sans-serif`;
                    ctx.fillStyle = this.color;
                    ctx.globalAlpha = this.opacity;
                    ctx.textBaseline = 'middle';
                    ctx.textAlign = 'center';

                    // 添加文字阴影
                    ctx.shadowColor = 'rgba(0,0,0,0.5)';
                    ctx.shadowBlur = 4;
                    ctx.shadowOffsetX = 1;
                    ctx.shadowOffsetY = 1;

                    const lines = this.watermarkText.split('\n').filter(line => line.trim() !== '');
                    const lineHeight = this.fontSize * 2 * 1.3;

                    lines.forEach((line, index) => {
                        const dy = (index - (lines.length - 1) / 2) * lineHeight;
                        ctx.fillText(line.trim(), x, y + dy);
                    });

                    ctx.globalAlpha = 1;
                    this.previewUrl = canvas.toDataURL('image/png');
                };

                img.src = this.imageUrl;
            },

            downloadImage() {
                if (!this.previewUrl) {
                    alert('图片还未生成，请稍等...');
                    return;
                }

                const a = document.createElement('a');
                a.href = this.previewUrl;
                a.download = 'watermarked-image.png';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            }
        },
        watch: {
            watermarkText() { this.updatePreview(); },
            fontSize() { this.updatePreview(); },
            color() { this.updatePreview(); },
            opacity() { this.updatePreview(); },
        },
    };
</script>

<style scoped>
    .image-watermark-tool {
        padding: 20px;
        border: 1px solid #ddd;
        border-radius: 8px;
        max-width: 800px;
        margin: 40px auto;
        font-family: Arial, sans-serif;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    }
    h3 {
        text-align: center;
        color: #333;
        margin-bottom: 20px;
    }
    .upload-area {
        text-align: center;
        padding: 30px;
        border: 2px dashed #ccc;
        border-radius: 8px;
        background-color: #f9f9f9;
        margin-bottom: 20px;
    }
    .upload-area input[type="file"] {
        margin-bottom: 10px;
        font-size: 14px;
    }
    .upload-area p {
        color: #666;
        font-size: 14px;
    }
    .controls {
        background: #f5f5f5;
        padding: 20px;
        border-radius: 6px;
        margin-bottom: 20px;
        box-shadow: inset 0 1px 3px rgba(0,0,0,0.1);
    }
    .form-group {
        margin-bottom: 15px;
    }
    .form-group label {
        display: block;
        margin-bottom: 5px;
        color: #333;
        font-weight: bold;
    }
    .form-group input,
    .form-group textarea {
        width: 100%;
        padding: 8px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 14px;
    }
    .form-row {
        display: flex;
        gap: 15px;
        flex-wrap: wrap;
    }
    .form-row .form-group {
        flex: 1;
        min-width: 150px;
    }
    .form-row input[type="range"] {
        width: 60%;
        vertical-align: middle;
    }
    .form-row span {
        font-size: 14px;
        color: #555;
    }
    .tip {
        font-size: 13px;
        color: #666;
        margin-top: 10px;
        font-style: italic;
    }
    .preview-container {
        margin-top: 20px;
        text-align: center;
    }
    .preview-container h4 {
        color: #333;
        margin-bottom: 10px;
    }
    .preview-wrapper {
        position: relative;
        display: inline-block;
        border: 1px solid #ddd;
        border-radius: 6px;
        overflow: hidden;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    }
    .preview-image {
        max-width: 100%;
        height: auto;
        display: block;
        border-radius: 6px;
    }
    .draggable-watermark {
        position: absolute;
        pointer-events: auto;
        white-space: pre-line;
        font-weight: bold;
        user-select: none;
        cursor: move;
        z-index: 10;
        padding: 5px 10px;
        border: 2px dashed rgba(255,255,255,0.5);
        border-radius: 4px;
        transition: border-color 0.2s;
    }
    .draggable-watermark:hover {
        border-color: rgba(66, 133, 244, 0.8);
    }
    .draggable-watermark:active {
        border-color: rgba(66, 133, 244, 1);
    }
    .download-btn {
        display: block;
        margin: 30px auto;
        padding: 12px 24px;
        font-size: 16px;
        background-color: #28a745;
        color: white;
        border: none;
        border-radius: 6px;
        cursor: pointer;
    }
    .download-btn:hover {
        background-color: #218838;
    }
    /* 内容说明区样式 */
    .tool-content {
        max-width: 800px;
        margin: 40px auto;
        padding: 30px;
        background: #f8f9fa;
        border-radius: 12px;
        text-align: left;
    }
    .tool-content h2 {
        color: #333;
        font-size: 1.5em;
        margin-bottom: 15px;
        border-bottom: 2px solid #00bcd4;
        padding-bottom: 10px;
    }
    .tool-content h3 {
        color: #555;
        font-size: 1.2em;
        margin: 20px 0 10px;
    }
    .tool-content p, .tool-content li {
        color: #666;
        line-height: 1.8;
        margin-bottom: 10px;
    }
    .tool-content ul, .tool-content ol {
        padding-left: 20px;
        margin-bottom: 15px;
    }
</style>